/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable no-console */
import {
  postChangePassword,
  postLoginUser,
  socialLogin,
  syncUserInCognito,
} from 'api/auth'
import AzureButtonImg from 'assets/azure-button.svg'
import GoogleButton from 'assets/google-button.svg'
import { useAuthContext } from 'auth/authContext'
import { confirmSignIn } from 'aws-amplify/auth'
import FormikPasswordField from 'components/formik/FormikPasswordField'
import FormikTextField from 'components/formik/FormikTextField'
import { Formik } from 'formik'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import Button from 'ui/atoms/Button'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import { getToken } from 'utils/auth'
import {
  error,
  initial,
  isError,
  isLoading,
  loading,
  success,
} from 'utils/remoteData'
import * as Yup from 'yup'

import { useFilters } from '@/hooks/useQuery'

import ActionLink from './components/ActionLink'
import AuthCard from './components/AuthCard'

type FormValues = {
  email: string
  password: string
  mfaCode?: string
}

type SubmitConfig = {
  retrying?: boolean
  overrideValues?: FormValues | null | undefined
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Please enter a valid email address'),
  password: Yup.string().required('Please enter a password'),
  mfaCode: Yup.string().optional(),
})

const initialValues = { email: '', password: '', mfaCode: '' }

function Login(): React.ReactNode {
  const history = useHistory()
  const { filters } = useFilters() as {
    filters: { code: string; state: string }
  }
  const socialCode = React.useMemo(() => filters?.code, [filters?.code])
  const state = React.useMemo(() => filters?.state, [filters?.state])
  const [newPasswordRequired, setNewPasswordRequired] = React.useState(false)
  const [mfaRequired, setMfaRequired] = React.useState(false)
  const socialLoginAttempted = React.useRef(false)

  const [loginRequest, setLoginRequest] = React.useState<{
    status: string
    error?: string
  }>(initial())

  const {
    redirectPath,
    clearRedirectPath,
    socialProviders,
    handleRedirectCallback,
  } = useAuthContext()

  const oauthButtons = React.useCallback((provider: string) => {
    if (provider === 'google-oauth2') {
      return <img alt="Login with Google" className="py-2" src={GoogleButton} />
    }
    if (provider === 'azuread-oauth2') {
      return (
        <img alt="Login with Azure" className="py-2" src={AzureButtonImg} />
      )
    }
    return null
  }, [])

  React.useEffect(() => {
    let mounted = true

    const redirectLoggedInUser = async () => {
      const token = await getToken()
      if (token && mounted) {
        redirectAfterLogin(history, redirectPath)
        clearRedirectPath()
      }
    }
    redirectLoggedInUser().catch(console.error)

    return () => {
      mounted = false
    }
  }, [history, redirectPath, clearRedirectPath])

  const onConfirmNewPassword = React.useCallback(
    async (values: { password: string; newPassword: string }) => {
      const { password, newPassword } = values

      try {
        setLoginRequest(loading())
        const response = await confirmSignIn({
          challengeResponse: newPassword,
        })
        if (response.isSignedIn) {
          await postChangePassword({
            password,
            newPassword,
          })
          await handleRedirectCallback()
          clearRedirectPath()
          setLoginRequest(success(response))
        }
      } catch (err: any) {
        toast.error(
          'Oops! Something went wrong. Please try again or contact support.'
        )
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        setLoginRequest(error(err.message))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onSubmit = React.useCallback(
    async (values: FormValues, config: SubmitConfig = {}) => {
      const { overrideValues, retrying } = config
      const email = overrideValues?.email || values.email
      const password = overrideValues?.password || values.password

      // If we are in the MFA challenge phase, confirm the MFA code:
      if (mfaRequired) {
        try {
          setLoginRequest(loading())
          // Confirm the MFA challenge using the code the user provided.
          const response = await confirmSignIn({
            challengeResponse: values.mfaCode || '',
          })
          if (response.isSignedIn) {
            await handleRedirectCallback()
            setLoginRequest(success(response))
          }
        } catch (err) {
          setLoginRequest(error('Incorrect MFA code.'))
          toast.error('Authentication failed: incorrect MFA code.')
        }
        return
      }

      // Normal login attempt:
      try {
        setLoginRequest(loading())
        const response = await postLoginUser({ email, password })
        if (response.isSignedIn && response.nextStep.signInStep === 'DONE') {
          // User is fully signed in.
          await handleRedirectCallback()
          setLoginRequest(success(response))
        } else if (
          [
            'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
            'RESET_PASSWORD',
          ].includes(response.nextStep.signInStep)
        ) {
          setNewPasswordRequired(true)
          setLoginRequest(error('Update Your Password for Enhanced Security'))
          toast.warning('Action Required')
        } else if (
          response.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
        ) {
          // Cognito indicates that MFA is required.
          setMfaRequired(true)
          setLoginRequest(success('MFA required'))
          toast.info('Please enter your MFA code.')
        } else {
          throw new Error(response.nextStep.signInStep)
        }
      } catch (err: any) {
        if (
          !retrying &&
          ['UserNotFoundException', 'NotAuthorizedException'].includes(err.name)
        ) {
          try {
            await syncUserInCognito(email, password)
            await onSubmit(values, { retrying: true })
          } catch (err: any) {
            const message = err.detail?.error || err.message
            if (message === 'Password reset required.') {
              await onSubmit(values)
            } else if (
              [
                'Your email is already linked for single sign-on (SSO).',
              ].includes(message)
            ) {
              setLoginRequest(error(message))
              toast.error('Authentication failed')
            } else {
              setLoginRequest(error('Incorrect email or password.'))
              toast.error('Authentication failed')
            }
          }
        } else {
          setLoginRequest(error('Incorrect email or password.'))
          toast.error('Authentication failed')
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mfaRequired]
  )

  const doSocialLogin = React.useCallback(
    async (code: string, provider: string) => {
      try {
        setLoginRequest(loading())
        const response = await socialLogin(code, provider)
        const { email, token } = response
        await onSubmit(initialValues, {
          retrying: true,
          overrideValues: { email, password: token },
        })
      } catch (err: any) {
        const message = err?.response?.data?.detail
        if (message === "Your credentials aren't allowed") {
          return setLoginRequest(
            error("You don't have access to this account.")
          )
        }
        setLoginRequest(
          error(
            'Oops! Something went wrong. Please try again or contact support.'
          )
        )
      }
    },
    [onSubmit]
  )

  React.useEffect(() => {
    let mounted = true
    const controller = new AbortController()

    async function fetchSocialLogin() {
      const socialProvider = state?.split(',')[0]

      if (
        socialCode &&
        socialProvider &&
        !socialLoginAttempted.current &&
        mounted
      ) {
        try {
          socialLoginAttempted.current = true
          await doSocialLogin(socialCode, socialProvider)
        } catch (err) {
          // Reset the attempt flag if there was an error and we're still mounted
          if (mounted) {
            socialLoginAttempted.current = false
          }
        }
      }
    }

    // Only attempt social login if we don't already have a token
    void getToken().then((token) => {
      if (!token && mounted) {
        fetchSocialLogin().catch(console.error)
      }
    })

    return () => {
      mounted = false
      controller.abort()
    }
  }, [socialCode, state, doSocialLogin])

  const submit = React.useCallback(
    async (values: {
      email: string
      password: string
      newPassword?: string
      mfaCode?: string
    }) => {
      if (newPasswordRequired) {
        await onConfirmNewPassword({
          password: values.password,
          newPassword: values.newPassword || '',
        })
      }
      await onSubmit(values)
    },
    [newPasswordRequired, onConfirmNewPassword, onSubmit]
  )

  const updateRedirectURI = React.useCallback(
    (providerLink: string) => {
      if (!redirectPath) return providerLink

      const url = new URL(providerLink)
      const state = url.searchParams.get('state')
      if (!state) return providerLink

      const newState = state + ',' + redirectPath

      url.searchParams.set('state', newState)

      return url.toString()
    },
    [redirectPath]
  )

  return (
    <>
      <Helmet>
        <title>Log In/Zentake</title>
      </Helmet>
      <AuthCard wide={false}>
        <Formik
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationSchema}
        >
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Space>
                <FormikTextField
                  id="email"
                  label="Email"
                  name="email"
                  placeholder="Type your email"
                />

                <FormikPasswordField
                  id="password"
                  label={newPasswordRequired ? 'Current Password ' : 'Password'}
                  name="password"
                  placeholder="Type your password"
                  type="password"
                />
                {newPasswordRequired && (
                  <FormikTextField
                    label="New Password"
                    name="newPassword"
                    placeholder="Type your new password"
                    type="password"
                  />
                )}
                {mfaRequired && (
                  <FormikTextField
                    label="MFA Code"
                    name="mfaCode"
                    placeholder="Enter your MFA code"
                  />
                )}
                <Button
                  $block
                  $primary
                  id="login-button"
                  loading={isLoading(loginRequest)}
                  type="submit"
                >
                  Login
                </Button>
              </Space>
              <Flex justify="between">
                <ActionLink to="/signup">Create an account</ActionLink>
                <ActionLink to="/forgot-password">
                  Forgot your password?
                </ActionLink>
              </Flex>
              {isError(loginRequest) && (
                <Flex justify="center">
                  <ErrorLabel>{loginRequest.error}</ErrorLabel>
                </Flex>
              )}

              {socialProviders.filter(
                (provider) => provider.enabled && provider.name !== 'cognito'
              ).length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center py-2">
                    <div className="h-px flex-grow bg-gray-400" />

                    <span className="flex-shrink px-4 text-2xl font-light text-gray-500">
                      or
                    </span>

                    <div className="h-px flex-grow bg-gray-400" />
                  </div>

                  <div className="flex flex-wrap items-center justify-center">
                    {socialProviders
                      .filter(
                        (provider) =>
                          provider.enabled && provider.name !== 'cognito'
                      )
                      .map((provider) => {
                        const { name, link } = provider
                        const updatedProviderLink = updateRedirectURI(link)

                        return (
                          <a
                            className="flex w-full justify-center"
                            href={
                              !isLoading(loginRequest)
                                ? updatedProviderLink
                                : '/login#'
                            }
                            key={name}
                          >
                            {oauthButtons(name)}
                          </a>
                        )
                      })}
                  </div>
                </div>
              )}
            </form>
          )}
        </Formik>
      </AuthCard>
    </>
  )
}

export default Login

type History = {
  push: (path: string) => void
}

function redirectToDashboard(history: History) {
  history.push('/')
}

function redirectAfterLogin(
  history: History,
  redirectPath: string | null | undefined
) {
  if (redirectPath) {
    history.push(redirectPath)
  } else {
    redirectToDashboard(history)
  }
}
